import React from "react";
import { Box, Button, Typography, Select, MenuItem, FormControl, InputLabel, useTheme, alpha } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export default function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) {
  const theme = useTheme();
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 7;

    if (totalPages <= maxPagesToShow) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, current page, and pages around current
      if (currentPage <= 3) {
        // Near the start
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalItems === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 3,
        padding: 2,
        backgroundColor: "white",
        borderRadius: 1,
        border: "1px solid #ddd",
        flexWrap: "wrap",
        gap: 2
      }}
    >
      {/* Rows per page selector */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Rows per page:
        </Typography>
        <Select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          size="small"
          sx={{ height: 30, fontSize: "0.875rem" }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </Box>

      {/* Pagination Controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Showing X-Y of Z */}
        <Typography variant="caption" color="text.secondary" sx={{ marginRight: 2 }}>
          Showing {startItem}-{endItem} of {totalItems}
        </Typography>

        {/* Previous Button */}
        <Button
          size="small"
          variant="text"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          startIcon={<ChevronLeftIcon />}
          sx={{ minWidth: "60px" }}
        >
          Prev
        </Button>

        {/* Page Numbers */}
        {getPageNumbers().map((page, index) => {
          if (page === "...") {
            return (
              <Typography key={`ellipsis-${index}`} variant="body2" color="text.secondary">
                ...
              </Typography>
            );
          }

          return (
            <Button
              key={page}
              size="small"
              variant={currentPage === page ? "contained" : "outlined"}
              onClick={() => onPageChange(page)}
              sx={{
                minWidth: "32px",
                height: "32px",
                padding: 0,
                backgroundColor: currentPage === page ? theme.palette.primary.main : "white",
                color: currentPage === page ? "white" : "#666",
                borderColor: currentPage === page ? theme.palette.primary.main : "rgba(0, 0, 0, 0.23)",
                "&:hover": {
                  backgroundColor: currentPage === page 
                    ? theme.palette.primary.dark 
                    : alpha(theme.palette.primary.main, 0.1),
                  borderColor: currentPage === page ? theme.palette.primary.dark : theme.palette.primary.main,
                },
              }}
            >
              {page}
            </Button>
          );
        })}

        {/* Next Button */}
        <Button
          size="small"
          variant="text"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          endIcon={<ChevronRightIcon />}
          sx={{ minWidth: "60px" }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
